import { useState } from 'react';
import { ServiceMultiSelect, BookingProgress, BookingSummaryRow } from '../../components/booking';
import { usePublicBookingSelection } from '../../hooks/usePublicBookingSelection';
import {
	formatBookingCurrency,
	formatLocationAddress,
	estimateDeposit,
} from '../../utils/bookingFormat';
import { formatSize, formatLength } from '../../constants/serviceOptions';
import type { PublicBookingInfo } from '../../types/publicBooking';

const STEPS = ['Client Info', 'Service', 'Review', 'Payment'];

interface BookWithPaymentProps {
	token: string;
	info: PublicBookingInfo;
}

interface ClientForm {
	firstName: string;
	lastName: string;
	phone: string;
	email: string;
}

const EMPTY_CLIENT: ClientForm = { firstName: '', lastName: '', phone: '', email: '' };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Book with payment — the deposit-taking booking link.
 *
 * Steps 1–3 are live: the client form, the service/size/length picker and the
 * cost summary all run off the same booking-token data as the walk-in page.
 * Step 4 is still presentational — the card fields are placeholders and
 * confirming does not call the create endpoint, pending the Stripe wiring that
 * `clientSecret` / `stripePublishableKey` from the create response will drive.
 */
const BookWithPayment = ({ token, info }: BookWithPaymentProps) => {
	// The token is what a live submit will post against; not needed until then.
	void token;

	const [currentStep, setCurrentStep] = useState(1);
	const [client, setClient] = useState<ClientForm>(EMPTY_CLIENT);
	const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ClientForm, string>>>({});
	const [stepError, setStepError] = useState<string | null>(null);
	const [notes, setNotes] = useState('');
	const [card, setCard] = useState({ name: '', number: '', expiry: '', cvv: '' });
	const [isConfirmed, setIsConfirmed] = useState(false);

	const selection = usePublicBookingSelection(info.services);

	const deposit = estimateDeposit(selection.serviceTotal);
	const balance = Math.max(selection.serviceTotal - deposit, 0);
	const address = formatLocationAddress(info);

	const updateClient = (field: keyof ClientForm, value: string) => {
		setClient(prev => ({ ...prev, [field]: value }));
		setFieldErrors(prev => ({ ...prev, [field]: undefined }));
	};

	const goToStep = (step: number) => {
		setStepError(null);
		setCurrentStep(step);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const handleContinueFromInfo = () => {
		const errors: Partial<Record<keyof ClientForm, string>> = {};
		if (!client.firstName.trim()) errors.firstName = 'First name is required.';
		if (!client.lastName.trim()) errors.lastName = 'Last name is required.';
		if (!client.phone.trim()) errors.phone = 'Phone number is required.';
		if (!client.email.trim()) errors.email = 'Email address is required.';
		else if (!EMAIL_PATTERN.test(client.email.trim())) errors.email = 'Enter a valid email.';
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		goToStep(2);
	};

	const handleContinueFromService = () => {
		if (selection.selectedServices.length === 0) {
			setStepError('Please select at least one service.');
			return;
		}
		if (!selection.size) {
			setStepError('Please select a size.');
			return;
		}
		if (!selection.length) {
			setStepError('Please select a length.');
			return;
		}
		goToStep(3);
	};

	const handleProceedToPayment = () => {
		setCard(prev => ({
			...prev,
			name: prev.name || `${client.firstName} ${client.lastName}`.trim(),
		}));
		goToStep(4);
	};

	const handlePayDeposit = () => {
		if (!card.name.trim()) {
			setStepError('Please enter the name on card.');
			return;
		}
		if (card.number.replace(/\s/g, '').length < 16) {
			setStepError('Please enter a valid 16-digit card number.');
			return;
		}
		if (card.expiry.replace(/\D/g, '').length < 4) {
			setStepError('Please enter a valid expiry date.');
			return;
		}
		if (card.cvv.trim().length < 3) {
			setStepError('Please enter a valid CVV.');
			return;
		}
		setStepError(null);
		setIsConfirmed(true);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const formatCardNumber = (value: string) =>
		value
			.replace(/\D/g, '')
			.substring(0, 16)
			.replace(/(.{4})/g, '$1 ')
			.trim();

	const formatExpiry = (value: string) => {
		const digits = value.replace(/\D/g, '').substring(0, 4);
		return digits.length >= 3 ? `${digits.substring(0, 2)} / ${digits.substring(2)}` : digits;
	};

	const serviceNames = selection.selectedServices.map(s => s.name).join(', ');

	return (
		<div className="pb pb--paid">
			{/* Topbar */}
			<div className="pb__topbar">
				<div className="pb__topbar-inner">
					<div className="pb__brand">
						<div className="pb__brand-logo">
							<svg width="22" height="22" viewBox="0 0 32 32" fill="none">
								<text
									x="4"
									y="23"
									fontFamily="Georgia,serif"
									fontSize="18"
									fontWeight="900"
									fill="currentColor"
								>
									B
								</text>
								<text
									x="13"
									y="23"
									fontFamily="Georgia,serif"
									fontSize="18"
									fontWeight="900"
									fill="currentColor"
									opacity="0.85"
								>
									B
								</text>
								<polygon points="26,16 31,19 26,22" fill="#f59e0b" />
							</svg>
						</div>
						<div>
							<div className="pb__brand-tagline">Book with</div>
							<div className="pb__brand-name">{info.providerName}</div>
						</div>
					</div>
				</div>
			</div>

			<div className="pb__content">
				<div className="pb__content-inner">
					{/* Page header */}
					<div className="pb__page-header">
						<div className="pb__page-header-icon">🚶</div>
						<div>
							<div className="pb__page-header-title">Book Appointment</div>
							<div className="pb__page-header-desc">
								Book your appointment with <strong>{info.providerName}</strong>
								{address && (
									<>
										{' '}
										at <strong>{address}</strong>
									</>
								)}
							</div>
						</div>
					</div>

					{!isConfirmed && (
						<BookingProgress steps={STEPS} currentStep={currentStep} showStepNumbers />
					)}

					{/* ── Step 1: Client Info ── */}
					{!isConfirmed && currentStep === 1 && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Client Information</div>
								<div className="pb__card-desc">Enter your details</div>
							</div>

							<div className="pb__form-grid">
								<TextField
									label="First Name"
									required
									value={client.firstName}
									onChange={value => updateClient('firstName', value)}
									placeholder="e.g. Kenya"
									error={fieldErrors.firstName}
								/>
								<TextField
									label="Last Name"
									required
									value={client.lastName}
									onChange={value => updateClient('lastName', value)}
									placeholder="e.g. Bennett"
									error={fieldErrors.lastName}
								/>
								<TextField
									label="Phone Number"
									required
									type="tel"
									value={client.phone}
									onChange={value => updateClient('phone', value)}
									placeholder="(718) 555-0000"
									error={fieldErrors.phone}
								/>
								<TextField
									label="Email Address"
									required
									type="email"
									value={client.email}
									onChange={value => updateClient('email', value)}
									placeholder="client@email.com"
									error={fieldErrors.email}
								/>
							</div>

							<div className="pb__actions">
								<div />
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromInfo}
								>
									Continue →
								</button>
							</div>
						</div>
					)}

					{/* ── Step 2: Service ── */}
					{!isConfirmed && currentStep === 2 && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Service Details</div>
								<div className="pb__card-desc">
									Choose your services, size and length
								</div>
							</div>

							{stepError && <ErrorBanner message={stepError} />}

							<div className="pb__section-label">
								Select Service
								<span className="pb__section-hint">(select all that apply)</span>
							</div>

							<ServiceMultiSelect
								services={info.services}
								selectedIds={selection.selectedIds}
								onToggle={selection.toggleService}
								onRemove={selection.removeService}
								categories={selection.categories}
								activeCategory={selection.activeCategory}
								onCategoryChange={selection.setActiveCategory}
							/>

							<div className="pb__divider" />

							<div className="pb__section-label">
								Select Size <span className="pb__req">*</span>
							</div>
							{selection.selectedServices.length === 0 ? (
								<div className="pb__empty-hint">
									Select a service to see the sizes it is offered in.
								</div>
							) : selection.hasNoSharedSize ? (
								<div className="pb__empty-hint">
									The selected services have no size in common. Please book them
									separately.
								</div>
							) : (
								<div className="pb__pill-group">
									{selection.sizeOptions.map(option => (
										<button
											key={option}
											type="button"
											className={`pb__pill${selection.size === option ? ' is-selected' : ''}`}
											onClick={() => selection.setSize(option)}
										>
											{formatSize(option)}
										</button>
									))}
								</div>
							)}

							<div className="pb__divider" />

							<div className="pb__section-label">
								Select Length <span className="pb__req">*</span>
							</div>
							{!selection.size ? (
								<div className="pb__empty-hint">Select a size first.</div>
							) : selection.hasNoSharedLength ? (
								<div className="pb__empty-hint">
									The selected services share no length at this size. Try another
									size, or book them separately.
								</div>
							) : (
								<div className="pb__pill-group">
									{selection.lengthOptions.map(option => (
										<button
											key={option}
											type="button"
											className={`pb__pill${selection.length === option ? ' is-selected' : ''}`}
											onClick={() => selection.setLength(option)}
										>
											{formatLength(option)}
										</button>
									))}
								</div>
							)}

							<div className="pb__divider" />
							<div>
								<label className="pb__label" htmlFor="paid-notes">
									Additional Notes
								</label>
								<textarea
									id="paid-notes"
									className="pb__textarea"
									value={notes}
									onChange={event => setNotes(event.target.value)}
									placeholder="Enter any special notes or requirements"
								/>
							</div>

							<div className="pb__actions">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(1)}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromService}
								>
									Review Booking →
								</button>
							</div>
						</div>
					)}

					{/* ── Step 3: Review ── */}
					{!isConfirmed && currentStep === 3 && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Review &amp; Confirm</div>
								<div className="pb__card-desc">
									Check your details before proceeding to payment
								</div>
							</div>

							<div className="pb__section-label">Client</div>
							<div className="pb__summary">
								<BookingSummaryRow
									label="Name"
									value={`${client.firstName} ${client.lastName}`}
								/>
								<BookingSummaryRow label="Phone" value={client.phone} />
								<BookingSummaryRow label="Email" value={client.email} />
							</div>

							<div className="pb__divider" />
							<div className="pb__section-label">Service</div>
							<div className="pb__summary">
								<BookingSummaryRow label="Services" value={serviceNames} />
								<BookingSummaryRow
									label="Size"
									value={formatSize(selection.size)}
								/>
								<BookingSummaryRow
									label="Length"
									value={formatLength(selection.length)}
								/>
								{notes.trim() && (
									<BookingSummaryRow
										label="Notes"
										value={notes.trim()}
										variant="normal"
									/>
								)}
							</div>

							<div className="pb-deposit">
								<div className="pb-deposit__row pb-deposit__row--top">
									<div className="pb-deposit__label">Service Total</div>
									<div className="pb-deposit__value">
										{formatBookingCurrency(selection.serviceTotal)}
									</div>
								</div>
								<div className="pb-deposit__row">
									<div className="pb-deposit__total-label">Deposit Amount</div>
									<div className="pb-deposit__total-value">
										{formatBookingCurrency(deposit)}
									</div>
								</div>
							</div>

							<div className="pb__actions">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(2)}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleProceedToPayment}
								>
									Proceed to Payment →
								</button>
							</div>
						</div>
					)}

					{/* ── Step 4: Payment (static) ── */}
					{!isConfirmed && currentStep === 4 && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Payment</div>
								<div className="pb__card-desc">
									Pay your deposit to confirm the appointment
								</div>
							</div>

							{stepError && <ErrorBanner message={stepError} />}

							<div className="pb__alert">
								<span className="pb__alert-icon">💳</span>
								<div className="pb__alert-body">
									A deposit is required to secure your appointment. The remaining
									balance is due on the day of your service.
								</div>
							</div>

							<div className="pb__summary">
								<BookingSummaryRow
									label="Service Total"
									value={formatBookingCurrency(selection.serviceTotal)}
								/>
								<BookingSummaryRow
									label="Deposit Due"
									value={formatBookingCurrency(deposit)}
									variant="positive"
								/>
								<BookingSummaryRow
									label="Balance Due on Day"
									value={formatBookingCurrency(balance)}
								/>
							</div>

							<div className="pb__divider" />
							<div className="pb__section-label">Card Details</div>

							<div className="pb__form-grid">
								<div className="pb__form-full">
									<TextField
										label="Name on Card"
										required
										value={card.name}
										onChange={value => setCard({ ...card, name: value })}
										placeholder="e.g. Kenya Bennett"
									/>
								</div>
								<div className="pb__form-full">
									<TextField
										label="Card Number"
										required
										value={card.number}
										onChange={value =>
											setCard({ ...card, number: formatCardNumber(value) })
										}
										placeholder="1234 5678 9012 3456"
									/>
								</div>
								<TextField
									label="Expiry"
									required
									value={card.expiry}
									onChange={value =>
										setCard({ ...card, expiry: formatExpiry(value) })
									}
									placeholder="MM / YY"
								/>
								<TextField
									label="CVV"
									required
									value={card.cvv}
									onChange={value =>
										setCard({
											...card,
											cvv: value.replace(/\D/g, '').substring(0, 4),
										})
									}
									placeholder="123"
								/>
							</div>

							<div className="pb__actions">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(3)}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--success"
									onClick={handlePayDeposit}
								>
									💳 Pay Deposit
								</button>
							</div>
						</div>
					)}

					{/* ── Confirmed ── */}
					{isConfirmed && (
						<div className="pb__card">
							<div className="pb__confirm-hero">
								<div className="pb__confirm-icon">✓</div>
								<div className="pb__confirm-title">Booking Confirmed!</div>
								<div className="pb__confirm-sub">
									Your deposit has been received. See you soon!
								</div>
							</div>

							<div className="pb__divider" />
							<div className="pb__section-label">Booking Summary</div>
							<div className="pb__summary">
								<BookingSummaryRow
									label="Client"
									value={`${client.firstName} ${client.lastName}`}
								/>
								<BookingSummaryRow label="Phone" value={client.phone} />
								<BookingSummaryRow label="Services" value={serviceNames} />
								<BookingSummaryRow
									label="Size / Length"
									value={`${formatSize(selection.size)} · ${formatLength(selection.length)}`}
								/>
								<BookingSummaryRow
									label="Service Total"
									value={formatBookingCurrency(selection.serviceTotal)}
								/>
								<BookingSummaryRow
									label="Deposit Paid"
									value={formatBookingCurrency(deposit)}
									variant="positive"
								/>
								<BookingSummaryRow
									label="Balance Due"
									value={formatBookingCurrency(balance)}
								/>
							</div>

							<div className="pb__actions pb__actions--center">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => window.location.reload()}
								>
									+ New Booking
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

// ── Local presentational helpers ────────────────────────────────

const TextField = ({
	label,
	value,
	onChange,
	placeholder,
	error,
	required,
	type = 'text',
}: {
	label: string;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	error?: string;
	required?: boolean;
	type?: string;
}) => {
	const id = `paid-${label.toLowerCase().replace(/\s+/g, '-')}`;
	return (
		<div>
			<label className="pb__label" htmlFor={id}>
				{label}
				{required && <span className="pb__req">*</span>}
			</label>
			<input
				id={id}
				className={`pb__input${error ? ' has-error' : ''}`}
				type={type}
				value={value}
				onChange={event => onChange(event.target.value)}
				placeholder={placeholder}
			/>
			{error && <span className="pb__field-error">{error}</span>}
		</div>
	);
};

const ErrorBanner = ({ message }: { message: string }) => (
	<div className="pb__error-banner">
		<span>⚠️</span>
		<span>{message}</span>
	</div>
);

export default BookWithPayment;
